import React from "react";

interface ContentfulImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  width?: number;
  height?: number;
  quality?: number;
  pictureClassName?: string;
}

const buildUrl = (
  base: string,
  fm: "avif" | "webp" | "jpg" | "png",
  q: number,
  w?: number,
  h?: number,
  dpr?: number
) => {
  const url = base.startsWith("//") ? `https:${base}` : base;
  const params = new URLSearchParams();
  if (w) params.set("w", String(w));
  if (h) params.set("h", String(h));
  params.set("fm", fm);
  params.set("q", String(q));

  if ((w || h) && dpr) params.set("dpr", String(dpr));

  return `${url}?${params.toString()}`;
};

const ContentfulImage: React.FC<ContentfulImageProps> = ({
  src,
  width,
  height,
  quality = 10,
  alt = "",
  loading = "lazy",
  decoding = "async",
  pictureClassName,
  ...imgProps
}) => {
  const avif1x = buildUrl(src, "avif", quality, width, height, 1);
  const avif2x = buildUrl(src, "avif", quality, width, height, 2);
  const webp1x = buildUrl(src, "webp", quality, width, height, 1);
  const webp2x = buildUrl(src, "webp", quality, width, height, 2);
  const fallback1x = buildUrl(src, "jpg", quality, width, height, 1);
  const fallback2x = buildUrl(src, "jpg", quality, width, height, 2);

  return (
    <picture className={pictureClassName}>
      <source type="image/avif" srcSet={`${avif1x} 1x, ${avif2x} 2x`} />
      <source type="image/webp" srcSet={`${webp1x} 1x, ${webp2x} 2x`} />

      <img
        src={fallback1x}
        srcSet={`${fallback1x} 1x, ${fallback2x} 2x`}
        width={width}
        height={height}
        alt={alt}
        loading={loading}
        decoding={decoding}
        {...imgProps}
      />
    </picture>
  );
};

export default ContentfulImage;
